import { useAtomValue } from 'jotai';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import { Temporal } from 'temporal-polyfill';
import { routes } from '~/api/routes';
import type { DropdownObjectItem } from '~/components/granular';
import { FetchError } from '~/errors/fetch-error';
import { userConfig } from '~/global-state/userConfig';
import { useFetch } from '~/hooks';
import { useFormatMessage } from '~/intl';
import type { Duration } from '~/schemas/enums';
import type { PerimeterConsumer, PerimeterProductionDevice } from '~/schemas/metrics';
import { perimeterSchema } from '~/schemas/metrics';
import type { DateRange } from '~/utils/temporal';

export type PerimeterDateRange = [string | null, string | null];

function objectToFilterList<T>(entityList: Array<T>, valueLabel: keyof T, valueKey: keyof T) {
  return entityList.map((entity) => ({
    label: entity[valueLabel] as string,
    value: entity[valueKey] as string,
    ...entity,
  }));
}

export function usePerimeterQuery() {
  const { timezone } = useAtomValue(userConfig);
  const { fetchData } = useFetch();
  const formatMessage = useFormatMessage();
  const [searchParams, setSearchParams] = useSearchParams();

  const [error, setError] = useState<Error | null>(null);
  // Loading until a fetch for the current search params has settled
  const [settledPerimeterKey, setSettledPerimeterKey] = useState<string | null>(null);
  const perimeterLoading = settledPerimeterKey !== searchParams.toString();
  const [initialPerimeterLoading, setInitialPerimeterLoading] = useState(true);

  const [consumers, setConsumers] = useState<Array<DropdownObjectItem & PerimeterConsumer>>([]);
  const [productionDevices, setProductionDevices] = useState<Array<DropdownObjectItem & PerimeterProductionDevice>>([]);
  const [durations, setDurations] = useState<Array<Duration>>([]);

  const perimeterDateRange: PerimeterDateRange = useMemo(() => {
    return [searchParams.get('start_datetime'), searchParams.get('end_datetime')];
  }, [searchParams]);

  const dateRange = useMemo((): DateRange => {
    const UTCStartDate = searchParams.get('start_datetime');
    const UTCEndDate = searchParams.get('end_datetime');

    if (!UTCStartDate || !UTCEndDate) {
      return [null, null];
    }

    const startDate = Temporal.Instant.from(UTCStartDate).toZonedDateTimeISO(timezone).toPlainDate();
    const endDate = Temporal.Instant.from(UTCEndDate).toZonedDateTimeISO(timezone).toPlainDate().add({ days: -1 });

    return [startDate, endDate];
  }, [searchParams, timezone]);

  const handlePerimeterDateChange = useCallback(
    (dates: DateRange) => {
      const [startDate, endDate] = dates;
      if (!startDate || !endDate) {
        return;
      }

      const UTCStartDate = startDate.toZonedDateTime(timezone).toInstant().toString();
      const UTCEndDate = endDate.toZonedDateTime(timezone).add({ days: 1 }).toInstant().toString();

      const params = new URLSearchParams(searchParams.toString());
      params.set('start_datetime', UTCStartDate);
      params.set('end_datetime', UTCEndDate);

      setSearchParams(params, {
        preventScrollReset: true,
      });
    },
    [searchParams, setSearchParams, timezone],
  );

  useEffect(() => {
    const startDate = searchParams.get('start_datetime');
    const endDate = searchParams.get('end_datetime');

    if (!startDate || !endDate) {
      return;
    }

    const perimeterKey = searchParams.toString();
    // A response for search params that have since changed must not overwrite the current ones
    let ignore = false;

    fetchData(
      routes.metrics.getPerimeter,
      { response: perimeterSchema },
      {
        queryParams: searchParams,
      },
    )
      .then((perimeterResponse) => {
        if (!ignore && perimeterResponse) {
          const perimeter = perimeterSchema.parse(perimeterResponse);

          const consumersList = objectToFilterList(perimeter.consumers_with_consumption, 'name', 'uuid');
          setConsumers(consumersList);

          const productionDevicesList = objectToFilterList(
            perimeter.production_devices_with_production,
            'name',
            'uuid',
          );
          setProductionDevices(productionDevicesList);

          setDurations(perimeter.durations);
          setInitialPerimeterLoading(false);
        }
      })
      .catch((error) => {
        if (ignore) {
          return;
        }

        const appInitError = new FetchError(formatMessage({ id: 'error.generic' }));
        appInitError.cause = error;

        setError(appInitError);
      })
      .finally(() => {
        if (!ignore) {
          setSettledPerimeterKey(perimeterKey);
        }
      });

    return () => {
      ignore = true;
    };
  }, [searchParams, fetchData, formatMessage]);

  if (error) {
    throw error;
  }

  return {
    consumers,
    dateRange,
    durations,
    handlePerimeterDateChange,
    initialPerimeterLoading,
    perimeterDateRange,
    perimeterLoading,
    productionDevices,
    timezone,
  };
}
